/**
 * Callback registry for trip dialog callbacks.
 * This allows us to store callbacks outside of Redux state (which doesn't allow non-serializable values).
 */

type TripCreateCallback = (
  trip: import("@/types/trip").Trip
) => void | Promise<void>

type CallbackId = string

class TripDialogCallbackRegistry {
  private callbacks = new Map<
    CallbackId,
    {
      onCreate?: TripCreateCallback
    }
  >()

  private nextId = 0

  /**
   * Register callbacks and return a unique ID
   */
  register(callbacks: { onCreate?: TripCreateCallback }): CallbackId {
    const id = `trip-dialog-callback-${this.nextId++}`
    this.callbacks.set(id, callbacks)
    return id
  }

  /**
   * Get callbacks by ID
   */
  get(id: CallbackId | undefined):
    | {
        onCreate?: TripCreateCallback
      }
    | undefined {
    if (!id) return undefined
    return this.callbacks.get(id)
  }

  /**
   * Unregister callbacks by ID
   */
  unregister(id: CallbackId | undefined): void {
    if (id) {
      this.callbacks.delete(id)
    }
  }

  /**
   * Clear all callbacks (useful for cleanup)
   */
  clear(): void {
    this.callbacks.clear()
  }
}

// Singleton instance
export const tripDialogCallbackRegistry = new TripDialogCallbackRegistry()
